import { unref } from 'vue';

import type { VisibleValue } from '@bv/types/common';

/**
 * Evaluates the `visible` property of a component that supports visibility control.
 * Handles boolean, ref, computed ref, or function returning any of these.
 *
 * @param visible - The visible value to evaluate (can be boolean, ref, computed ref, or function)
 * @returns `true` if the item should be visible, `false` otherwise. Defaults to `true` if undefined.
 *
 * @example
 * ```ts
 * // Boolean
 * evaluateVisibility(true) // returns true
 * evaluateVisibility(false) // returns false
 *
 * // Ref
 * const isVisible = ref(true);
 * evaluateVisibility(isVisible) // returns true
 *
 * // Computed
 * const isVisible = computed(() => someCondition);
 * evaluateVisibility(isVisible) // returns computed value
 *
 * // Function
 * evaluateVisibility(() => ref(true)) // returns true
 * ```
 */
export const evaluateVisibility = (visible?: VisibleValue): boolean => {
  if (visible === undefined) {
    return true;
  }

  if (typeof visible === 'function') {
    const result = visible();
    return unref(result);
  }

  return unref(visible);
};
